import { IdentitySource } from '@infiltre/shared';
import { type Api, createApi } from '@/shared/lib/api';
import type { Identity, IdentityAcquireRequest, IdentityProvider } from './types';
import { clearWebToken, getWebToken, setWebToken } from './web-token';

const formatErr = (err: { value: unknown }): string => {
  const v = err.value;
  if (typeof v === 'string') return v;
  if (v && typeof v === 'object' && 'error' in v && typeof v.error === 'string') return v.error;
  return JSON.stringify(v);
};

/** Web identity provider — anon session via `POST /auth/anon`, token parked in sessionStorage. */
export const createWebIdentityProvider = (apiBaseUrl: string): IdentityProvider => {
  let api: Api | null = null;
  const getApi = (): Api => {
    if (!api) api = createApi(apiBaseUrl, getWebToken);
    return api;
  };

  const acquire = async (request?: IdentityAcquireRequest): Promise<Identity> => {
    const nickname = request?.nickname?.trim();
    if (!nickname) throw new Error('identity_failed: nickname required for anon login');
    const res = await getApi().auth.anon.post({ name: nickname });
    if (res.error) throw new Error(formatErr(res.error));
    const data = res.data as { token: string; playerId: string; name: string };
    setWebToken(data.token);
    return { playerId: data.playerId, name: data.name, source: IdentitySource.Anon };
  };

  const restore = async (): Promise<Identity | null> => {
    const token = getWebToken();
    if (!token) return null;
    try {
      const res = await getApi().auth.me.get();
      if (res.error) {
        clearWebToken();
        return null;
      }
      const data = res.data as { playerId: string; name: string; source: IdentitySource } | null;
      if (!data) {
        clearWebToken();
        return null;
      }
      return { playerId: data.playerId, name: data.name, source: data.source };
    } catch {
      clearWebToken();
      return null;
    }
  };

  const clear = async (): Promise<void> => {
    clearWebToken();
  };

  return {
    acquire,
    restore,
    clear,
    getToken: getWebToken,
    setToken: setWebToken,
    source: IdentitySource.Anon,
  };
};
